import { motion } from "framer-motion";
import { ArrowLeft, Pencil } from "lucide-react";
import { lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";

const LazyVideo = lazy(() => import("@/components/LazyVideo"));

interface HeroSectionProps {
  onCtaClick?: () => void;
}

const HeroSection = ({ onCtaClick }: HeroSectionProps) => {
  const navigate = useNavigate();

  const handleCta = () => {
    if (onCtaClick) {
      onCtaClick();
    } else {
      document.getElementById("register")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section dir="rtl">
      {/* Top section */}
      <div className="px-4 pt-10 pb-8">
        <div>
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center justify-center gap-1.5 mb-4"
          >
            <Pencil className="w-4 h-4 text-primary" />
            <p className="text-primary text-sm font-bold">לחשוב בקו</p>
          </motion.div>

          {/* Main Headline */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-center mb-3"
          >
            <h1 className="text-foreground text-4xl md:text-5xl font-bold leading-tight">מהראש ליד.</h1>
            <p className="text-lg md:text-xl font-bold text-foreground leading-relaxed mt-2">
              שיטה פשוטה להפוך רעיון לסקיצה ברורה.
            </p>
          </motion.div>

          {/* Subtitle */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="text-center mb-4"
          >
            <p className="text-base md:text-lg font-bold text-foreground leading-relaxed">
              יש לכם רעיון בראש
              <br />
              אבל קשה להסביר אותו?
            </p>
            <p className="text-base md:text-lg font-medium text-foreground/75 leading-relaxed mt-2">
              סקיצה הופכת אותו לברור.
            </p>
            <p className="text-base md:text-lg font-medium text-foreground/75 leading-relaxed mt-1">
              לפני CAD ולפני AI.
            </p>
          </motion.div>

          {/* Top CTA */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="grid grid-cols-2 gap-2 px-2 max-w-md mx-auto"
          >
            <motion.button
              onClick={handleCta}
              animate={{
                boxShadow: [
                  "0 0 8px 2px hsl(var(--primary) / 0.3)",
                  "0 0 16px 6px hsl(var(--primary) / 0.5)",
                  "0 0 8px 2px hsl(var(--primary) / 0.3)",
                ],
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="flex items-center justify-center gap-2 px-3 py-3.5 bg-primary text-primary-foreground font-bold text-sm sm:text-base rounded-xl hover:bg-primary/90 transition-all active:scale-[0.97]"
            >
              <ArrowLeft className="w-5 h-5 shrink-0" />
              מפגש חינמי
            </motion.button>
            <button
              onClick={() => navigate("/offer")}
              className="flex items-center justify-center gap-2 px-3 py-3.5 border-2 border-primary text-primary font-bold text-sm sm:text-base rounded-xl hover:bg-primary/5 transition-all active:scale-[0.97]"
            >
              הקורסים
            </button>
          </motion.div>

          {/* Marilena Coming Soon */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="text-center mt-6 space-y-1"
          >
            <span className="bg-primary/15 text-primary text-xs font-bold px-2.5 py-0.5 rounded-full">בקרוב</span>
            <p className="text-sm font-bold text-foreground pt-1">מרילנה – מנטורית סקיצה אישית 24/7</p>
            <p className="text-xs text-muted-foreground">מצלמים סקיצה ← מקבלים פידבק ← מתקדמים</p>
          </motion.div>
        </div>
      </div>

      {/* Hand drawing animation */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="relative z-10 mb-0"
      >
        <div className="relative border-y border-white">
          <video autoPlay loop muted playsInline preload="metadata" className="w-full object-cover" src="/Sequence_01_1.mp4" />
        </div>
      </motion.div>

      {/* Xylophone video */}
      <Suspense fallback={<div className="w-full bg-muted" style={{ aspectRatio: "4/1" }} />}>
        <LazyVideo
          src="/Thinking_in_a_line_ksilofhon_02.mp4"
          className="relative w-full overflow-hidden border-y border-white"
          style={{ aspectRatio: "4/1" }}
          rootMargin="400px"
        />
      </Suspense>

      {/* Second Video Block */}
      <Suspense fallback={<div className="w-full bg-muted" style={{ aspectRatio: "2/1" }} />}>
        <LazyVideo
          src="/Thinking_in_a_Line_Home_Page_s-size.mp4"
          className="relative w-full overflow-hidden border-y border-white"
          style={{ aspectRatio: "2/1" }}
          rootMargin="400px"
        />
      </Suspense>
    </section>
  );
};

export default HeroSection;
